import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import ReceiptPosition from '../../../../../../../../../types/api/receipt-position'
import { ReceiptStatus } from '../../../../../../../../../types/api/receipt'
import { Button, Flex, InputNumber, Popconfirm, Select, Table, notification } from 'antd'
import { CheckOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/es/table'
import _ from 'lodash'
import {
    useCreateReceiptPositionMutation,
    useUpdateReceiptPositionMutation,
} from '../../../../../../../../../store/api/receipt-positions-api'
import {
    useLazyGetReceiptByDocIdQuery,
    useUpdateReceiptMutation,
} from '../../../../../../../../../store/api/receipts-api'
import { EndpointQueryParameters } from '../../../../../../../../../types/strapi/strapi'
import { useGetUsersQuery } from '../../../../../../../../../store/api/users-api'
import {
    useCreateOrderRequestMutation,
    useUpdateOrderRequestMutation,
} from '../../../../../../../../../store/api/order-requests-api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetMaterialTypesQuery, materialTypesApi } from '../../../../../../../../../store/api/material-types-api'
import { useCreateStockMutation } from '../../../../../../../../../store/api/stocks-api'
import { useGetMaterialGroupsQuery } from '../../../../../../../../../store/api/material-groups-api'
import { filterNestedOptions } from '../../../../../../../../../helpers/filter-nested-options'
import {
    createValidateMaterialWidth,
    findMaterialDocumentId,
} from '../../../../../../../material-order-requests/components/material-order-requests/helpers/parse-material-width-value'
import { resolveMaterialDocumentId } from '../../../../../../../material-order-requests/components/material-order-requests/helpers/resolve-material-document-id'
import { useCreateMaterialMutation } from '../../../../../../../../../store/api/materials-api'
import { orderRequestType } from '../../../../../../../../../constants/entity-enums/order-request-types'
import MaterialType from '../../../../../../../../../types/api/material-type'
import { useUser } from '../../../../../../../../../hooks/use-user'
import { useAppDispatch } from '../../../../../../../../../store/store'
import { stocksApi } from '../../../../../../../../../store/api/stocks-api'
import { buildMaterialWidthOptions } from '../../../create-receipt-form/helpers/build-material-width-options'
import PositionWidthInput from './components/position-width-input'

const DRAFT_ROW_ID = '__draft__'

interface DraftPosition {
    materialTypeDocId?: string
    material?: string
    initiator?: string
    amount?: number
}

interface RowEditState {
    materialTypeDocId: string
    material: string
    initiator: string
    amount: number
}

type TableRow = ReceiptPosition & { isDraft?: boolean }

interface ReceiptPositionEditProps {
    dataSource?: ReceiptPosition[]
    receiptQueryParams: EndpointQueryParameters
    receiptId: string
    receiptStatus?: ReceiptStatus
}

const userSelectOptions = (users?: { documentId: string; username: string }[]) =>
    users?.map(item => ({ label: item.username, value: item.documentId })) ?? []

const mapPositionToRowState = (position: ReceiptPosition): RowEditState => ({
    materialTypeDocId: position.material.material_type.documentId,
    material: String(position.material.width),
    initiator:
        position.initiator?.documentId ?? position.order_request?.initiator?.documentId ?? '',
    amount: position.amount,
})

const ReceiptPositionEdit: FC<ReceiptPositionEditProps> = ({
    dataSource,
    receiptQueryParams,
    receiptId,
    receiptStatus,
}) => {
    // Принятая позиция уже в остатках, а позиции закрытого заказа не принять — бэкенд их не меняет
    const isReceiptClosed = receiptStatus === 'Завершен' || receiptStatus === 'Отменен'
    const isLocked = (record: ReceiptPosition) =>
        isReceiptClosed || record.positionStatus === 'Принята'

    const user = useUser()
    const dispatch = useAppDispatch()
    const [draft, setDraft] = useState<DraftPosition | null>(null)
    const [rowEdits, setRowEdits] = useState<Record<string, RowEditState>>({})
    const [isSavingDraft, setIsSavingDraft] = useState(false)

    const [getReceiptByDocId] = useLazyGetReceiptByDocIdQuery()
    const [updateReceipt] = useUpdateReceiptMutation()
    const [updateReceiptPosition] = useUpdateReceiptPositionMutation()
    const [createReceiptPosition] = useCreateReceiptPositionMutation()
    const [createOrderRequest] = useCreateOrderRequestMutation()
    const [updateOrderRequest] = useUpdateOrderRequestMutation()
    const [createMaterial] = useCreateMaterialMutation()
    const [createStock] = useCreateStockMutation()
    const { data: users } = useGetUsersQuery()
    const materialGroupsParams = useMemo(
        () => ({
            fields: ['name'],
            populate: {
                material_types: {
                    fields: ['name'],
                    ...(user?.documentId
                        ? {
                              filters: {
                                  purchaser: { documentId: { $eq: user.documentId } },
                              },
                          }
                        : {}),
                },
            },
        }),
        [user?.documentId]
    )
    const { data: materialGroupsData } = useGetMaterialGroupsQuery(materialGroupsParams)

    const { data: draftMaterialTypeData } = useGetMaterialTypesQuery(
        draft?.materialTypeDocId
            ? { filters: { documentId: { $eq: draft.materialTypeDocId } }, populate: { unit: true } }
            : skipToken
    )
    const draftMaterialType = draftMaterialTypeData?.data[0] as MaterialType | undefined

    useEffect(() => {
        if (!dataSource) return
        setRowEdits(
            Object.fromEntries(dataSource.map(position => [position.documentId, mapPositionToRowState(position)]))
        )
    }, [dataSource])

    const refreshReceipt = () => getReceiptByDocId([receiptId, receiptQueryParams])

    const updateRowEdit = (documentId: string, patch: Partial<RowEditState>) => {
        setRowEdits(prev => ({
            ...prev,
            [documentId]: { ...prev[documentId], ...patch },
        }))
    }

    const resolveMaterialForRow = useCallback(
        async (materialTypeDocId: string, materialValue: string) => {
            const stocksResult = await dispatch(
                stocksApi.endpoints.getStocks.initiate({
                    filters: {
                        material: { material_type: { documentId: { $eq: materialTypeDocId } } },
                    },
                    populate: '*',
                    pagination: { page: 1, pageSize: 1000 },
                })
            ).unwrap()

            const stocks = stocksResult.data || []
            const materials = stocks.map(item => item.material)
            const materialOptions = buildMaterialWidthOptions(stocks)

            const existingMaterialId = findMaterialDocumentId(materialValue, materialOptions, materials)
            const materialDocumentId = await resolveMaterialDocumentId({
                materialTypeDocId,
                materialValue,
                createMaterial: body => createMaterial(body).then(res => ({ data: res.data })),
                materials,
                materialOptions,
            })

            if (!materialDocumentId) return null

            if (!existingMaterialId) {
                await createStock({
                    data: { material: { connect: [materialDocumentId] }, amount: 0 },
                })
            }

            const materialTypeResult = await dispatch(
                materialTypesApi.endpoints.getMaterialTypes.initiate({
                    filters: { documentId: { $eq: materialTypeDocId } },
                    populate: { unit: true },
                })
            ).unwrap()
            const unitDocumentId = (materialTypeResult.data?.[0] as MaterialType | undefined)?.unit
                ?.documentId

            return { materialDocumentId, unitDocumentId }
        },
        [createMaterial, createStock, dispatch]
    )

    const saveRowMaterial = async (record: ReceiptPosition) => {
        const row = rowEdits[record.documentId]
        if (!row?.materialTypeDocId || !row.material) return

        try {
            const resolved = await resolveMaterialForRow(row.materialTypeDocId, row.material)
            if (!resolved?.materialDocumentId || !resolved.unitDocumentId) {
                notification.error({ message: 'Не удалось определить материал' })
                return
            }

            const res = await updateReceiptPosition({
                docId: record.documentId,
                body: {
                    data: {
                        material: { set: [resolved.materialDocumentId] },
                        unit: { set: [resolved.unitDocumentId] },
                    },
                },
            })

            if (record.order_request?.documentId) {
                await updateOrderRequest({
                    docId: record.order_request.documentId,
                    body: {
                        data: {
                            material: { set: [resolved.materialDocumentId] },
                            unit: { set: [resolved.unitDocumentId] },
                        },
                    },
                })
            }

            if (res.data) refreshReceipt()
        } catch (e) {
            console.error(e)
            notification.error({ message: 'Не удалось обновить материал' })
        }
    }

    const saveRowAmount = useMemo(
        () =>
            _.debounce(async (record: ReceiptPosition, amount: number) => {
                try {
                    const res = await updateReceiptPosition({
                        docId: record.documentId,
                        body: { data: { amount } },
                    })
                    if (record.order_request?.documentId) {
                        await updateOrderRequest({
                            docId: record.order_request.documentId,
                            body: { data: { amount } },
                        })
                    }
                    if (res.data) refreshReceipt()
                } catch (e) {
                    console.error(e)
                }
            }, 600),
        // refreshReceipt пересоздаётся на каждом рендере — с ним debounce пересоздавался бы и терял отложенный вызов
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [updateReceiptPosition, updateOrderRequest]
    )

    const onInitiatorChange = async (record: ReceiptPosition, initiator: string) => {
        updateRowEdit(record.documentId, { initiator })
        try {
            const res = await updateReceiptPosition({
                docId: record.documentId,
                body: { data: { initiator: { set: [initiator] } } },
            })
            if (record.order_request?.documentId) {
                await updateOrderRequest({
                    docId: record.order_request.documentId,
                    body: { data: { initiator: { set: [initiator] } } },
                })
            }
            if (res.data) refreshReceipt()
        } catch (e) {
            console.error(e)
        }
    }

    const onMaterialTypeChange = (record: ReceiptPosition, materialTypeDocId: string) => {
        updateRowEdit(record.documentId, { materialTypeDocId, material: '' })
    }

    const onDeletePosition = async (record: ReceiptPosition) => {
        if (!dataSource) return
        const nextIds = dataSource
            .filter(item => item.documentId !== record.documentId)
            .map(item => item.documentId)
        try {
            const res = await updateReceipt({
                docId: receiptId,
                body: { data: { receipt_positions: { set: nextIds } } },
            })
            if (res.data) {
                refreshReceipt()
                notification.success({ message: 'Позиция удалена из заказа' })
            }
        } catch (e) {
            console.error(e)
        }
    }

    const validateDraftAsync = async (): Promise<string | null> => {
        if (!draft?.materialTypeDocId) return 'Выберите тип материала'
        if (!draft.material) return 'Укажите ширину'
        if (!draft.initiator) return 'Выберите инициатора'
        if (!draft.amount || draft.amount <= 0) return 'Укажите количество'

        const stocksResult = await dispatch(
            stocksApi.endpoints.getStocks.initiate({
                filters: {
                    material: { material_type: { documentId: { $eq: draft.materialTypeDocId } } },
                },
                populate: '*',
                pagination: { page: 1, pageSize: 1000 },
            })
        ).unwrap()
        const stocks = stocksResult.data || []
        const materials = stocks.map(item => item.material)
        const materialOptions = buildMaterialWidthOptions(stocks)
        const validate = createValidateMaterialWidth(materialOptions, materials)

        try {
            await validate(null, draft.material)
        } catch (error) {
            if (error instanceof Error) return error.message
            return 'Некорректная ширина'
        }

        return null
    }

    const onSaveDraft = async () => {
        if (!draft) return

        const validationError = await validateDraftAsync()
        if (validationError) {
            notification.error({ message: validationError })
            return
        }

        const unitDocumentId = draftMaterialType?.unit?.documentId
        if (!unitDocumentId) {
            notification.error({ message: 'Не удалось определить единицу измерения' })
            return
        }

        setIsSavingDraft(true)
        try {
            const resolved = await resolveMaterialForRow(draft.materialTypeDocId!, draft.material!)
            if (!resolved?.materialDocumentId) return

            const orderRequestResult = await createOrderRequest({
                data: {
                    goodsType: orderRequestType[0],
                    requestStatus: 'Новый',
                    amount: draft.amount!,
                    initiator: { connect: [draft.initiator!] },
                    material: { connect: [resolved.materialDocumentId] },
                    unit: { connect: [unitDocumentId] },
                },
            }).unwrap()
            const orderRequestDocId = orderRequestResult.data?.documentId
            if (!orderRequestDocId) return

            const positionResult = await createReceiptPosition({
                data: {
                    positionType: 'Материал',
                    unit: { connect: [unitDocumentId] },
                    amount: draft.amount!,
                    material: { connect: [resolved.materialDocumentId] },
                    initiator: { connect: [draft.initiator!] },
                    order_request: { connect: [orderRequestDocId] },
                },
            }).unwrap()
            const positionDocId = positionResult.data?.documentId
            if (!positionDocId) return

            await updateReceipt({
                docId: receiptId,
                body: { data: { receipt_positions: { connect: [positionDocId] } } },
            })
            await updateOrderRequest({
                docId: orderRequestDocId,
                body: { data: { requestStatus: 'Обработан' } },
            })

            setDraft(null)
            refreshReceipt()
            notification.success({ message: 'Позиция добавлена' })
        } catch (e) {
            console.error(e)
            notification.error({ message: 'Не удалось добавить позицию' })
        } finally {
            setIsSavingDraft(false)
        }
    }

    const onAddDraftRow = () => {
        setDraft({
            materialTypeDocId: undefined,
            material: undefined,
            initiator: user?.documentId,
            amount: undefined,
        })
    }

    const updateDraft = (patch: Partial<DraftPosition>) => {
        setDraft(prev => ({ ...prev, ...patch }))
    }

    useEffect(() => {
        if (draft?.materialTypeDocId) {
            setDraft(prev => (prev ? { ...prev, material: undefined } : prev))
        }
    }, [draft?.materialTypeDocId])

    const materialGroupOptions = (materialGroupsData?.data || []).filter(
        group => (group.material_types?.length ?? 0) > 0
    )

    const renderMaterialSelect = (
        value: string | undefined,
        onChange: (materialTypeDocId: string) => void,
        disabled?: boolean
    ) => (
        <Select
            style={{ width: '100%' }}
            disabled={disabled}
            showSearch
            filterOption={filterNestedOptions}
            placeholder={'Материал'}
            popupMatchSelectWidth={400}
            value={value}
            onChange={onChange}
        >
            {materialGroupOptions.map(group => (
                <Select.OptGroup key={group.id} label={group.name}>
                    {group.material_types?.map(type => (
                        <Select.Option key={type.id} value={type.documentId}>
                            {type.name}
                        </Select.Option>
                    ))}
                </Select.OptGroup>
            ))}
        </Select>
    )

    const columns: ColumnsType<TableRow> = [
        {
            title: 'Материал',
            width: 220,
            render: (_, record) => {
                if (record.isDraft) {
                    return renderMaterialSelect(draft?.materialTypeDocId, materialTypeDocId =>
                        updateDraft({ materialTypeDocId })
                    )
                }
                const row = rowEdits[record.documentId]
                if (!row) return null
                return renderMaterialSelect(
                    row.materialTypeDocId,
                    materialTypeDocId => onMaterialTypeChange(record, materialTypeDocId),
                    isLocked(record)
                )
            },
        },
        {
            title: 'Ширина',
            width: 140,
            render: (_, record) => {
                if (record.isDraft) {
                    return (
                        <PositionWidthInput
                            materialTypeDocId={draft?.materialTypeDocId}
                            value={draft?.material}
                            onChange={material => updateDraft({ material })}
                        />
                    )
                }
                const row = rowEdits[record.documentId]
                if (!row) return null
                return (
                    <PositionWidthInput
                        materialTypeDocId={row.materialTypeDocId}
                        value={row.material}
                        disabled={isLocked(record)}
                        onChange={material => updateRowEdit(record.documentId, { material })}
                        onBlur={() => saveRowMaterial(record)}
                    />
                )
            },
        },
        {
            title: 'Инициатор',
            width: 180,
            render: (_, record) => {
                if (record.isDraft) {
                    return (
                        <Select
                            style={{ width: '100%' }}
                            showSearch
                            optionFilterProp={'label'}
                            placeholder={'Инициатор'}
                            value={draft?.initiator}
                            options={userSelectOptions(users)}
                            onChange={initiator => updateDraft({ initiator })}
                        />
                    )
                }
                const row = rowEdits[record.documentId]
                if (!row) return null
                return (
                    <Select
                        style={{ width: '100%' }}
                        showSearch
                        optionFilterProp={'label'}
                        value={row.initiator}
                        disabled={isLocked(record)}
                        options={userSelectOptions(users)}
                        onChange={initiator => onInitiatorChange(record, initiator)}
                    />
                )
            },
        },
        {
            title: 'Кол-во',
            width: 130,
            render: (_, record) => {
                if (record.isDraft) {
                    return (
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0.1}
                            value={draft?.amount}
                            onChange={amount => updateDraft({ amount: amount ?? undefined })}
                        />
                    )
                }
                const row = rowEdits[record.documentId]
                if (!row) return null
                return (
                    <InputNumber
                        style={{ width: '100%' }}
                        min={0.1}
                        value={row.amount}
                        disabled={isLocked(record)}
                        addonAfter={record.unit?.shortName}
                        onChange={amount => {
                            if (amount === null) return
                            updateRowEdit(record.documentId, { amount })
                            saveRowAmount(record, amount)
                        }}
                    />
                )
            },
        },
        {
            title: '',
            width: 72,
            align: 'center',
            fixed: 'right',
            render: (_, record) => {
                if (record.isDraft) {
                    return (
                        <Flex gap={4} justify={'center'}>
                            <Button
                                type={'text'}
                                size={'small'}
                                icon={<CheckOutlined />}
                                style={{ color: '#52c41a' }}
                                title={'Сохранить позицию'}
                                loading={isSavingDraft}
                                onClick={onSaveDraft}
                            />
                            <Button
                                type={'text'}
                                size={'small'}
                                danger
                                icon={<DeleteOutlined />}
                                title={'Отменить'}
                                disabled={isSavingDraft}
                                onClick={() => setDraft(null)}
                            />
                        </Flex>
                    )
                }
                return (
                    <Popconfirm
                        title={'Удалить позицию?'}
                        description={'Позиция будет отвязана от заказа'}
                        onConfirm={() => onDeletePosition(record)}
                        okText={'Да'}
                        cancelText={'Нет'}
                        disabled={isLocked(record)}
                    >
                        <Button
                            type={'text'}
                            size={'small'}
                            danger
                            icon={<DeleteOutlined />}
                            title={'Удалить'}
                            disabled={isLocked(record)}
                        />
                    </Popconfirm>
                )
            },
        },
    ]

    const sortedPositions = dataSource
        ? _.cloneDeep(dataSource).sort((a, b) =>
              a.material.material_type.name.localeCompare(b.material.material_type.name, 'ru')
          )
        : []

    const draftRow: TableRow | null = draft
        ? ({ documentId: DRAFT_ROW_ID, isDraft: true, positionType: 'Материал' } as TableRow)
        : null

    const tableDataSource = draftRow ? [...sortedPositions, draftRow] : sortedPositions

    return (
        <Flex vertical gap={12}>
            <Table
                bordered
                columns={columns}
                dataSource={tableDataSource}
                rowKey={x => x.documentId}
                pagination={false}
                scroll={{ x: 820 }}
            />
            <Flex justify={'flex-end'}>
                <Button
                    type={'dashed'}
                    icon={<PlusOutlined />}
                    onClick={onAddDraftRow}
                    disabled={!!draft || isReceiptClosed}
                >
                    Добавить позицию
                </Button>
            </Flex>
        </Flex>
    )
}

export default ReceiptPositionEdit
